/**
 * Culians — Bag Drawer (global)
 *
 * Handles the slide-in bag drawer that lives on every page.
 *
 * Triggers (drawer opens):
 *   - Clicking any link to /cart in the page (header BAG link, etc.)
 *   - "cart:update" event fires from product form (after ADD TO BAG succeeds)
 *
 * In-drawer interactions:
 *   - Quantity +/-  → AJAX /cart/change.js → refetch this section's HTML
 *   - Remove link   → AJAX /cart/change.js with quantity=0 → refetch
 *   - Close (link, backdrop, ESC) → close drawer
 *
 * The CHECKOUT button and "Continue browsing" / empty-state links are
 * intentionally NOT wired beyond closing the drawer — the user wants
 * to design the full-page bag and checkout flow with references later.
 *
 * Section rendering: After every cart change we fetch
 *   /?sections=culians-bag-drawer
 * which returns the updated section HTML (Shopify Section Rendering API).
 * We then replace the inner content and footer of the drawer in place,
 * preserving the drawer's open state and scroll position.
 */
(function () {
  'use strict';

  var SECTION_ID = 'culians-bag-drawer';

  function getDrawer() {
    return document.getElementById('culians-bag');
  }

  /* ─── Open / close ──────────────────────────────────────── */

  function openDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;
    // Culians: after refreshDrawer() the aside is a freshly inserted node.
    // Reading a layout property forces the browser to compute its closed
    // state (translateX(100%)) before we flip data-open, so the slide-in
    // transition runs instead of the panel appearing instantly.
    var aside = drawer.querySelector('.culians-bag__drawer');
    if (aside) { void aside.offsetWidth; }
    drawer.setAttribute('data-open', 'true');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('culians-bag-open');
  }

  function closeDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;
    cancelAutoClose();
    drawer.setAttribute('data-open', 'false');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('culians-bag-open');
  }

  /* ─── Auto-close after ADD TO BAG (Suzan, 2026-09-08) ────────
   * Only the open triggered by add-to-bag auto-closes; a drawer the
   * customer opened from the header stays until dismissed. The
   * timer pauses while the pointer or keyboard focus is inside the
   * drawer and restarts when it leaves; a touch inside cancels it.
   */
  var AUTO_CLOSE_MS = 5000;
  var autoCloseTimer = null;
  var autoCloseArmed = false;

  function startAutoClose() {
    clearTimeout(autoCloseTimer);
    autoCloseTimer = setTimeout(function () {
      if (autoCloseArmed) closeDrawer();
    }, AUTO_CLOSE_MS);
  }

  function pauseAutoClose() {
    clearTimeout(autoCloseTimer);
    autoCloseTimer = null;
  }

  function cancelAutoClose() {
    pauseAutoClose();
    autoCloseArmed = false;
  }

  function armAutoClose() {
    var drawer = getDrawer();
    if (!drawer) return;
    autoCloseArmed = true;
    startAutoClose();
    if (!drawer.hasAttribute('data-culians-autoclose-bound')) {
      drawer.setAttribute('data-culians-autoclose-bound', '');
      var aside = function () { return drawer.querySelector('.culians-bag__drawer'); };
      drawer.addEventListener('mouseover', function (e) {
        var a = aside();
        if (autoCloseArmed && a && a.contains(e.target)) pauseAutoClose();
      });
      drawer.addEventListener('mouseout', function (e) {
        var a = aside();
        if (!autoCloseArmed || !a) return;
        if (!a.contains(e.relatedTarget)) startAutoClose();
      });
      drawer.addEventListener('focusin', function () { if (autoCloseArmed) pauseAutoClose(); });
      drawer.addEventListener('focusout', function (e) {
        var a = aside();
        if (autoCloseArmed && a && !a.contains(e.relatedTarget)) startAutoClose();
      });
      drawer.addEventListener('touchstart', function () { cancelAutoClose(); }, { passive: true });
    }
  }

  /* ─── Update the BAG link in the header with the live item count ─
   * Example: "Bag" → "Bag (3)". Stores the base text in a data
   * attribute so we can rebuild correctly when count returns to 0.
   */
  function updateHeaderBagCount(itemCount) {
    var links = document.querySelectorAll('[data-culians-bag-link]');
    Array.prototype.forEach.call(links, function (link) {
      var baseText = link.getAttribute('data-bag-base-text');
      if (!baseText) {
        // Fallback: extract from current text (strip any trailing "(N)")
        baseText = link.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
        link.setAttribute('data-bag-base-text', baseText);
      }
      link.textContent = itemCount > 0
        ? baseText + ' (' + itemCount + ')'
        : baseText;
    });
  }

  /* ─── Re-render the drawer's contents after a cart change ─
   * Uses Shopify's Section Rendering API. We fetch the rendered
   * section HTML and swap the drawer's inner content + footer
   * (everything inside #culians-bag) in place, keeping the wrapper
   * so the open animation state isn't disturbed.
   *
   * Also extracts the new item count from the fetched HTML and updates
   * the header bag link in sync (so "Bag (3)" → "Bag (2)" reflects
   * immediately after a remove, for example).
   */
  /* Resolve once every <img> inside `root` is fetched and decoded (or
   * after `capMs`, whichever comes first). Images are forced eager so a
   * detached lazy <img> actually loads. */
  function decodeImages(root, capMs) {
    if (!root) return Promise.resolve();
    var imgs = Array.prototype.slice.call(root.querySelectorAll('img'));
    if (!imgs.length) return Promise.resolve();
    var work = imgs.map(function (img) {
      img.loading = 'eager';
      if (typeof img.decode !== 'function') return Promise.resolve();
      return img.decode().catch(function () {});
    });
    var cap = new Promise(function (resolve) { setTimeout(resolve, capMs || 800); });
    return Promise.race([Promise.all(work), cap]);
  }

  function refreshDrawer() {
    var drawer = getDrawer();
    if (!drawer) return Promise.resolve();

    drawer.setAttribute('data-updating', 'true');

    return fetch('/?sections=' + SECTION_ID, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Section fetch failed');
        return response.json();
      })
      .then(function (data) {
        var html = data[SECTION_ID];
        if (!html) return;

        // Parse the returned HTML and find the new drawer root.
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newDrawer = doc.getElementById('culians-bag');
        if (!newDrawer) return;

        // Preserve the current open state on the existing drawer.
        var wasOpen = drawer.getAttribute('data-open') === 'true';

        // IMPORTANT — extract the new count BEFORE replaceWith.
        // The count span lives inside .culians-bag__drawer. Once we
        // move newAside into the live DOM, it's no longer a descendant
        // of the parsed newDrawer node — newDrawer.querySelector would
        // return null and the header would reset to "Bag" with no
        // count. Reading the count first sidesteps the order-of-ops
        // trap. (The full-page bag relies on this fix too.)
        var countSpan = newDrawer.querySelector('[data-culians-bag-count-wrap]');
        var match = countSpan ? countSpan.textContent.match(/\((\d+)\)/) : null;
        var count = match ? parseInt(match[1], 10) : 0;

        var currentAside = drawer.querySelector('.culians-bag__drawer');
        var newAside = newDrawer.querySelector('.culians-bag__drawer');

        // Flicker fix (2026-09-10): the swap used to insert fresh <img>
        // elements that then fetched/decoded on screen, so every line's
        // thumbnail blinked on each add. Fetch + decode the new images
        // while the old markup is still showing, then swap in one paint.
        // Capped so a slow image can't hold the drawer back.
        return decodeImages(newAside, 800).then(function () {
          // Swap the inner aside (so backdrop + animation state stay intact
          // and only the visible drawer content updates).
          if (currentAside && newAside) {
            currentAside.replaceWith(newAside);
          }

          // Restore open state (the markup defaults to closed).
          if (wasOpen) {
            drawer.setAttribute('data-open', 'true');
            drawer.setAttribute('aria-hidden', 'false');
          }

          // Update the header BAG link count to match the new state.
          updateHeaderBagCount(count);
        });
      })
      .catch(function (err) {
        console.error('Culians bag refresh error:', err);
      })
      .then(function () {
        drawer.removeAttribute('data-updating');
      });
  }

  /* ─── Cart AJAX operations ──────────────────────────────── */

  function changeLine(lineIndex, quantity) {
    var drawer = getDrawer();
    if (drawer) drawer.setAttribute('data-updating', 'true');

    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: lineIndex, quantity: quantity })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart change failed');
        return response.json();
      })
      .then(function () {
        return refreshDrawer();
      })
      .catch(function (err) {
        console.error('Culians bag change error:', err);
        if (drawer) drawer.removeAttribute('data-updating');
      });
  }

  /* ─── Event delegation ──────────────────────────────────── */

  function init() {
    var drawer = getDrawer();
    if (!drawer) return;

    // Guard: only init once
    if (drawer.getAttribute('data-culians-bag-init') === 'true') return;
    drawer.setAttribute('data-culians-bag-init', 'true');

    // ── Open drawer when any /cart link is clicked anywhere on the page
    // (the BAG header link uses href="{{ routes.cart_url }}", which is /cart).
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href]');
      if (!link) return;
      var href = link.getAttribute('href');
      if (!href) return;

      // Match /cart, /cart/, or trailing query/hash variants — but NOT /cart/*
      // (e.g. /cart/checkout) so we don't intercept actual checkout links.
      var matchesCartLink = /^\/cart\/?($|\?|#)/.test(href);
      if (!matchesCartLink) return;

      // Don't intercept clicks inside the drawer itself
      if (e.target.closest('#culians-bag')) return;

      e.preventDefault();
      cancelAutoClose(); // a deliberate open never auto-closes
      openDrawer();
    });

    // ── Open drawer after "Add to bag" succeeds
    // Atelier dispatches cart:update on the section after a successful add.
    document.addEventListener('cart:update', function (e) {
      var detail = e.detail && e.detail.data;
      // Only react to product-form additions (not in-drawer changes — we already
      // refresh the drawer ourselves for those, and don't want to re-open).
      if (!detail || detail.source !== 'product-form-component') return;
      if (detail.didError) return;
      refreshDrawer().then(function () {
        openDrawer();
        armAutoClose();
      });
    });

    // ── Close interactions (delegated to the drawer root)
    // For <a> elements with an href, let the browser navigate naturally —
    // the page reload that follows will dismiss the drawer anyway, so we
    // don't need to call closeDrawer() OR preventDefault(). For buttons
    // and divs (Close, backdrop), we close the drawer in-place.
    drawer.addEventListener('click', function (e) {
      var closer = e.target.closest('[data-culians-bag-close]');
      if (!closer) return;

      // Link with a real destination → navigate naturally
      if (closer.tagName === 'A' && closer.getAttribute('href')) {
        return;
      }

      // Buttons / backdrop → in-place close
      e.preventDefault();
      closeDrawer();
    });

    // ── ESC key closes
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.getAttribute('data-open') === 'true') {
        closeDrawer();
      }
    });

    // ── Proceed to Checkout button → goes to Shopify checkout
    drawer.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-culians-bag-checkout]');
      if (!btn) return;
      e.preventDefault();
      window.location.href = '/checkout';
    });

    // ── Remove link (delegated)
    // Quantity changes (+/-) are intentionally not wired here. Quantity
    // is display-only in the drawer; to change qty the customer goes to
    // View Bag (the full-page bag, designed separately). Remove still
    // works from the drawer because removal is binary.
    drawer.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-culians-bag-remove]');
      if (!btn) return;
      var lineIndex = parseInt(btn.getAttribute('data-line-index'), 10);
      if (!lineIndex) return;
      changeLine(lineIndex, 0);
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init after Shopify section re-renders (theme editor, etc.)
  document.addEventListener('shopify:section:load', function (e) {
    if (e.detail && e.detail.sectionId === SECTION_ID) {
      // The section was re-rendered fresh — re-init to attach handlers
      // to the new DOM. (Our init() is guarded against double-attach.)
      var drawer = document.getElementById('culians-bag');
      if (drawer) drawer.removeAttribute('data-culians-bag-init');
      init();
    }
  });

  // Back-forward cache (2026-10-02): returning from checkout with the
  // browser Back button restores this page from memory, so the drawer
  // can show the bag as it was at an earlier moment (even empty) while
  // the header count says otherwise. Checkout can also change the cart.
  // On any restored page, re-fetch the drawer from the live cart.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) refreshDrawer();
  });

  // Expose openDrawer in case other scripts want to trigger it
  // (e.g. a future "Quick view → Add to bag" flow).
  window.CulianBag = {
    open: openDrawer,
    close: closeDrawer,
    refresh: refreshDrawer
  };
})();
